"use client";

import { useState } from "react";
import { Checkbox } from "@/components/tool-kit/primitives";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import {
  toSmallCaps,
  toSubscript,
  toSuperscript,
} from "@/lib/tool-kit/text/small-text";

/** English strings. A translated content file overrides these through `ui`. */
export const SMALL_TEXT_GENERATOR_STRINGS = {
  inputText: "Input Text",
  placeholder: "Type or paste your text here...",
  results: "Results",
  superscriptLabel: "Superscript (ᵗⁱⁿʸ)",
  superscriptNote:
    "Raised q, C, F, Q and S only entered Unicode in versions 14 and 17 and most fonts lack them, so they stay full size unless you tick the option above. There is no raised capital X, Y or Z at all.",
  smallCapsLabel: "Small caps (ᴀʙᴄ)",
  smallCapsNote:
    "No small-cap X exists (a regular x is kept). Small-cap Q is drawn as the lookalike ǫ, which nearly every font has; tick the option above for the true small-cap ꞯ (Unicode 11), which some fonts lack.",
  subscriptLabel: "Subscript (ₐₑₓ)",
  subscriptNote:
    "Subscript only exists for digits and the letters a, e, h, i, j, k, l, m, n, o, p, r, s, t, u, v, x — everything else stays full size.",
  newestLetters:
    "Use the newest Unicode letters (ꞯ, 𐞥 ꟲ ꟳ ꟴ ꟱) — they may show as empty boxes on many devices",
  copied: "Copied!",
  copy: "Copy",
  typeAboveToSeeOutput: "Type above to see output…",
} as const;

function ResultCard({
  label,
  value,
  note,
}: {
  label: string;
  value: string;
  note?: string;
}) {
  const strings = useToolUi(SMALL_TEXT_GENERATOR_STRINGS);
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    if (value.length === 0) {
      return;
    }
    writeToClipboard(value).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center justify-between gap-2">
        <p className="font-medium text-foreground text-sm">{label}</p>
        <button
          className="rounded-md border border-border px-3 py-1 text-muted-foreground text-xs transition-colors hover:bg-accent hover:text-foreground disabled:opacity-40"
          disabled={value.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copy}
        </button>
      </div>
      <p className="mt-2 min-h-6 whitespace-pre-wrap break-words text-foreground text-sm leading-relaxed">
        {value.length > 0 ? (
          value
        ) : (
          <span className="text-muted-foreground">
            {strings.typeAboveToSeeOutput}
          </span>
        )}
      </p>
      {note && <p className="mt-2 text-muted-foreground text-xs">{note}</p>}
    </div>
  );
}

export default function SmallTextGeneratorWidget() {
  const strings = useToolUi(SMALL_TEXT_GENERATOR_STRINGS);
  const [input, setInput] = useState("");
  const [newest, setNewest] = useState(false);
  const options = { newest };

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="small-input"
        >
          {strings.inputText}
        </label>
        <textarea
          className="min-h-[140px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="small-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.placeholder}
          value={input}
        />
      </div>

      <Checkbox
        checked={newest}
        label={strings.newestLetters}
        onChange={setNewest}
      />

      {/* Results */}
      <section className="space-y-4">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.results}
        </h2>
        <div className="space-y-3">
          <ResultCard
            label={strings.superscriptLabel}
            note={strings.superscriptNote}
            value={toSuperscript(input, options)}
          />
          <ResultCard
            label={strings.smallCapsLabel}
            note={strings.smallCapsNote}
            value={toSmallCaps(input, options)}
          />
          <ResultCard
            label={strings.subscriptLabel}
            note={strings.subscriptNote}
            value={toSubscript(input)}
          />
        </div>
      </section>
    </>
  );
}
